import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Pipeline {
  id: string
  name: string
  title: string
  description: string
  source: string
  destination: string
  schedule: string
  status: string
  last_run: string
  avg_duration_minutes: number
  records_processed_last: number
  success_rate: number
}

interface Dataset {
  id: string
  name: string
  description: string
  schema: string
  type: string
  row_count: number
  size_gb: number
  last_updated: string
}

export default function DataWarehousePage() {
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [datasets, setDatasets] = useState<Dataset[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [jobs, setJobs] = useState<any[]>([])

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [pipelinesRes, datasetsRes, statsRes, jobsRes] = await Promise.all([
      fetch(API_BASE + '/data-warehouse/pipelines'),
      fetch(API_BASE + '/data-warehouse/datasets'),
      fetch(API_BASE + '/data-warehouse/statistics'),
      fetch(API_BASE + '/data-warehouse/jobs')
    ])
    if (pipelinesRes.ok) setPipelines((await pipelinesRes.json()).pipelines || [])
    if (datasetsRes.ok) setDatasets((await datasetsRes.json()).datasets || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
    if (jobsRes.ok) setJobs((await jobsRes.json()).jobs || [])
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'paused': return 'bg-yellow-100 text-yellow-800'
      case 'completed': return 'bg-green-100 text-green-800'
      case 'running': return 'bg-blue-100 text-blue-800'
      case 'failed': return 'bg-red-100 text-red-800'
      case 'queued': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatBytes = (gb: number) => {
    if (gb >= 1000) return `${(gb / 1000).toFixed(1)} TB`
    return `${gb.toFixed(1)} GB`
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Data Warehouse</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Pipeline</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pipelines</div>
              <div className="text-2xl font-bold">{statistics.total_pipelines}</div>
              <div className="text-xs text-green-600">{statistics.active_pipelines} active</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Datasets</div>
              <div className="text-2xl font-bold">{statistics.total_datasets}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Storage</div>
              <div className="text-2xl font-bold text-purple-600">{formatBytes(statistics.total_storage_gb)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Records</div>
              <div className="text-2xl font-bold text-blue-600">{(statistics.total_records / 1000000).toFixed(1)}M</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">ETL Pipelines</h2></div>
              <div className="divide-y">
                {pipelines.map(pipeline => (
                  <div key={pipeline.id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold">{pipeline.title}</div>
                        <div className="text-sm text-gray-500">{pipeline.description}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(pipeline.status)}`}>{pipeline.status}</span>
                    </div>
                    <div className="flex gap-2 mb-2">
                      <span className="px-2 py-0.5 bg-blue-100 rounded text-xs">{pipeline.source}</span>
                      <span className="text-gray-400">→</span>
                      <span className="px-2 py-0.5 bg-purple-100 rounded text-xs">{pipeline.destination}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs text-gray-500">
                      <div>Schedule: {pipeline.schedule}</div>
                      <div>Duration: {pipeline.avg_duration_minutes}min</div>
                      <div>Records: {pipeline.records_processed_last.toLocaleString()}</div>
                      <div>Success: {pipeline.success_rate}%</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Datasets</h2></div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schema</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rows</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {datasets.slice(0, 6).map(dataset => (
                    <tr key={dataset.id}>
                      <td className="px-6 py-4">
                        <div className="font-medium font-mono text-sm">{dataset.name}</div>
                        <div className="text-xs text-gray-500">{dataset.description}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{dataset.schema}</span>
                      </td>
                      <td className="px-6 py-4 text-sm">{(dataset.row_count / 1000000).toFixed(2)}M</td>
                      <td className="px-6 py-4 text-sm">{dataset.size_gb.toFixed(1)} GB</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Jobs</h3>
            <div className="space-y-3">
              {jobs.slice(0, 8).map(job => (
                <div key={job.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-mono">{job.id.slice(0, 12)}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(job.status)}`}>{job.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {job.records_read?.toLocaleString()} rows read
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
